/**
 * Export the current Word document as a PDF blob using the Office JS API.
 */
export async function exportCurrentDocumentAsPdf(): Promise<Blob> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Pdf,
      { sliceSize: 65536 },
      (result: Office.AsyncResult<Office.File>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const file = result.value;
          getFileSlices(file).then(resolve).catch(reject);
        } else {
          reject(new Error(result.error?.message || "Failed to export document"));
        }
      }
    );
  });
}

/**
 * Read all slices from an Office.File and assemble into a Blob.
 */
function getFileSlices(file: Office.File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const sliceCount = file.sliceCount;
    const slices: Uint8Array[] = [];
    let slicesReceived = 0;

    function getSlice(index: number): void {
      file.getSliceAsync(index, (sliceResult: Office.AsyncResult<Office.Slice>) => {
        if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
          slices[index] = new Uint8Array(sliceResult.value.data as ArrayBuffer);
          slicesReceived++;

          if (slicesReceived === sliceCount) {
            file.closeAsync();
            const blob = new Blob(slices, { type: "application/pdf" });
            resolve(blob);
          }
        } else {
          file.closeAsync();
          reject(new Error(sliceResult.error?.message || `Failed to get slice ${index}`));
        }
      });
    }

    for (let i = 0; i < sliceCount; i++) {
      getSlice(i);
    }
  });
}

/**
 * Get the current document's filename.
 */
export function getDocumentName(): string {
  const url = Office.context.document.url;
  if (url) {
    const parts = url.split(/[/\\]/);
    const filename = parts[parts.length - 1];
    // Remove extension and return
    return filename.replace(/\.[^/.]+$/, "");
  }
  return "Untitled Document";
}
